import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  GlassWater,
  Radio,
  RefreshCw,
  Sparkles,
  Tags,
  Trophy,
  User,
  Users,
  Wine,
  type LucideIcon,
} from 'lucide-react';
import { can, type Capability } from '@cercle/shared';
import { useApp } from '../app-context';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { useAppReload } from '../pwa/useAppReload';
import { SyncPill } from '../ui';
import { UpdateBanner } from './UpdateBanner';
import styles from './AppShell.module.scss';

/** Les cinq entrées de la barre basse : Séance · Cave · Noter · Classements · Profil. */
const NAV = [
  { to: '/', label: 'Séance', Icon: GlassWater, end: true },
  { to: '/cave', label: 'Cave', Icon: Wine, end: false },
  { to: '/noter', label: 'Noter', Icon: Sparkles, end: false },
  { to: '/classements', label: 'Classements', Icon: Trophy, end: false },
  { to: '/profil', label: 'Profil', Icon: User, end: false },
] as const;

const ADMIN_NAV = [
  { to: '/regie/seance', label: 'Régie', Icon: Radio, capability: 'session:manage' },
  { to: '/regie/membres', label: 'Membres', Icon: Users, capability: 'member:manage' },
  { to: '/regie/referentiel', label: 'Référentiel', Icon: Tags, capability: 'reference:manage' },
] as const satisfies readonly { to: string; label: string; Icon: LucideIcon; capability: Capability }[];

// Le prototype dessine ses icônes au trait fin — 1,5 —, pas au trait épais du design
// system Organic dont il hérite. C'est la maquette qui fait foi.
const ICON = { size: 22, strokeWidth: 1.5 } as const;

// La pastille de sync est bien plus petite que la navigation : son icône suit, en gardant
// le trait fin de 1,5 qui fait foi.
const SYNC_ICON = { size: 14, strokeWidth: 1.5 } as const;

export function AppShell(): ReactNode {
  const { member } = useApp();
  // La barre ne propose que ce que le rôle permet : un lien vers un 403 est une
  // promesse rompue. La même table sert ici et côté API.
  const nav = member
    ? ADMIN_NAV.filter((entry) => can(member.role, entry.capability))
    : [];

  return (
    <div className={styles.shell}>
      {/* Barre latérale — desktop uniquement, 268 px */}
      <aside className={styles.sidebar}>
        <Brand />

        <nav className={styles.sideNav} aria-label="Navigation principale">
          {NAV.map(({ to, label, Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                [styles.sideLink, isActive && styles.sideLinkActive].filter(Boolean).join(' ')
              }
            >
              <Icon {...ICON} aria-hidden="true" />
              {label}
            </NavLink>
          ))}
        </nav>

        {nav.length > 0 ? (
          <nav className={styles.sideNav} aria-label="Régie">
            {nav.map(({ to, label, Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  [styles.sideLink, isActive && styles.sideLinkActive].filter(Boolean).join(' ')
                }
              >
                <Icon {...ICON} aria-hidden="true" />
                {label}
              </NavLink>
            ))}
          </nav>
        ) : null}

        <div className={styles.sideSync}>
          <SyncIndicator />
        </div>
      </aside>

      <div className={styles.column}>
        <div className={styles.statusBar}>
          <Clock />
          <SyncIndicator />
        </div>

        <div className={styles.mobileBrand}>
          <Brand />
        </div>

        <main className={styles.main}>
          <Outlet />
        </main>
      </div>

      {/* Barre basse — masquée en desktop par le CSS, pas par le JavaScript */}
      <nav className={styles.bottomNav} aria-label="Navigation principale">
        {NAV.map(({ to, label, Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              [styles.bottomLink, isActive && styles.bottomLinkActive].filter(Boolean).join(' ')
            }
          >
            <Icon {...ICON} aria-hidden="true" />
            {label}
          </NavLink>
        ))}
      </nav>

      <UpdateBanner />
    </div>
  );
}

function Brand(): ReactNode {
  return (
    <div className={styles.brand}>
      {/* Le sceau, jamais posé sur un aplat cuivre — le référentiel l'interdit. */}
      <img className={styles.brandMark} src="/icons/icon-192.png" alt="" width={35} height={35} />
      <span className={styles.brandText}>
        <strong>Cercle des Anges</strong>
        <span>In Fusto Veritas</span>
      </span>
    </div>
  );
}

/** L'heure de la barre d'état, comme sur les maquettes mobiles. */
function Clock(): ReactNode {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(timer);
  }, []);

  return (
    <span className={styles.clock}>
      {now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
    </span>
  );
}

/**
 * « Hors-ligne · 2 en attente » / « Sync ✓ ».
 *
 * C'est le seul retour qu'a le membre sur le sort de sa note, et la maquette le place en
 * haut de tous les écrans. Le sauge pour confirmer, le cuivre pour l'attente, le rouge
 * pour l'abandon — les trois couleurs disent l'état sans qu'on ait à lire.
 */
function SyncIndicator(): ReactNode {
  const { sync, flushQueue, acknowledgeDiscarded } = useApp();
  const online = useOnlineStatus();
  const { reload, busy } = useAppReload();

  // Un seul geste, quel que soit l'état affiché : on solde ce qui traîne, puis on recharge.
  // Attendre la file avant de recharger évite de rejouer les saisies au prochain démarrage —
  // elles ne seraient pas perdues, IndexedDB les garde, mais le membre verrait « en attente »
  // reparaître après un clic censé tout solder.
  const handleClick = async (): Promise<void> => {
    if (sync.discarded > 0) acknowledgeDiscarded();
    if (sync.pending > 0) await flushQueue();
    await reload();
  };

  const pill = {
    onClick: () => void handleClick(),
    icon: <RefreshCw {...SYNC_ICON} aria-hidden="true" />,
    spinning: busy || sync.state === 'syncing',
    label: "Synchroniser et recharger l'application",
  };

  if (sync.discarded > 0) {
    return (
      <SyncPill state="error" {...pill}>
        {sync.discarded} saisie{sync.discarded > 1 ? 's' : ''} abandonnée
        {sync.discarded > 1 ? 's' : ''}
      </SyncPill>
    );
  }

  if (!online) {
    return (
      <SyncPill state="pending" {...pill}>
        Hors-ligne{sync.pending > 0 ? ` · ${sync.pending} en attente` : ''}
      </SyncPill>
    );
  }

  if (sync.state === 'syncing' || sync.pending > 0) {
    return (
      <SyncPill state="pending" {...pill}>
        {sync.pending > 0 ? `${sync.pending} en attente` : 'Synchronisation…'}
      </SyncPill>
    );
  }

  return (
    <SyncPill state="done" {...pill}>
      Sync ✓
    </SyncPill>
  );
}
